// This list of locales is based on the supported list of locales from the our localization pipeline.
// In order to add a new locale to this list we need to make sure it's also enabled in the localization pipeline.
// https://office.visualstudio.com/OC/_git/office-bohemia?path=%2Fcommon%2Fconfig%2Fazure-pipelines%2FREADME.md&_a=preview&anchor=office-bohemia---localization
export enum Locale {
  afZa = 'af-za',
  amEt = 'am-et',
  arSa = 'ar-sa',
  asIn = 'as-in',
  azLatnAz = 'az-latn-az',
  bgBg = 'bg-bg',
  bnIn = 'bn-in',
  bsLatnBa = 'bs-latn-ba',
  caEs = 'ca-es',
  caEsValencia = 'ca-es-valencia',
  csCz = 'cs-cz',
  cyGb = 'cy-gb',
  daDk = 'da-dk',
  deDe = 'de-de',
  elGr = 'el-gr',
  enGb = 'en-gb',
  enUs = 'en-us',
  esEs = 'es-es',
  esMx = 'es-mx',
  etEe = 'et-ee',
  euEs = 'eu-es',
  faIr = 'fa-ir',
  fiFi = 'fi-fi',
  filPh = 'fil-ph',
  frCa = 'fr-ca',
  frFr = 'fr-fr',
  gaIe = 'ga-ie',
  gdGb = 'gd-gb',
  glEs = 'gl-es',
  guIn = 'gu-in',
  heIl = 'he-il',
  hiIn = 'hi-in',
  hrHr = 'hr-hr',
  huHu = 'hu-hu',
  idId = 'id-id',
  isIs = 'is-is',
  itIt = 'it-it',
  jaJp = 'ja-jp',
  kaGe = 'ka-ge',
  kkKz = 'kk-kz',
  kmKh = 'km-kh',
  knIn = 'kn-in',
  kokIn = 'kok-in',
  koKr = 'ko-kr',
  lbLu = 'lb-lu',
  loLa = 'lo-la',
  ltLt = 'lt-lt',
  lvLv = 'lv-lv',
  miNz = 'mi-nz',
  mkMk = 'mk-mk',
  mlIn = 'ml-in',
  mrIn = 'mr-in',
  msMy = 'ms-my',
  mtMt = 'mt-mt',
  nbNo = 'nb-no',
  neNp = 'ne-np',
  nlNl = 'nl-nl',
  nnNo = 'nn-no',
  orIn = 'or-in',
  paIn = 'pa-in',
  plPl = 'pl-pl',
  ptBr = 'pt-br',
  ptPt = 'pt-pt',
  quzPe = 'quz-pe',
  roRo = 'ro-ro',
  ruRu = 'ru-ru',
  skSk = 'sk-sk',
  slSi = 'sl-si',
  sqAl = 'sq-al',
  srCyrlBa = 'sr-cyrl-ba',
  srCyrlRs = 'sr-cyrl-rs',
  srLatnRs = 'sr-latn-rs',
  svSe = 'sv-se',
  taIn = 'ta-in',
  teIn = 'te-in',
  thTh = 'th-th',
  trTr = 'tr-tr',
  ttRu = 'tt-ru',
  ugCn = 'ug-cn',
  ukUa = 'uk-ua',
  urPk = 'ur-pk',
  viVn = 'vi-vn',
  zhCn = 'zh-cn',
  zhTw = 'zh-tw'
}

// Mapping from locales that we don't support to locales that we do support.
// https://microsoft.sharepoint.com/sites/globalreadiness/SitePages/Language-fallback.aspx
const secondaryLocales: { [simplifiedLocale: string]: Locale } = {
  af: Locale.afZa,
  am: Locale.amEt,
  ar: Locale.arSa,
  as: Locale.asIn,
  az: Locale.azLatnAz,
  bg: Locale.bgBg,
  bn: Locale.enGb,
  bs: Locale.bsLatnBa,
  'bs-cyrl-ba': Locale.enGb,
  ca: Locale.caEs,
  cs: Locale.csCz,
  cy: Locale.cyGb,
  da: Locale.daDk,
  de: Locale.deDe,
  el: Locale.elGr,
  'el-cy': Locale.enGb,
  en: Locale.enUs,
  es: Locale.esEs,
  et: Locale.etEe,
  eu: Locale.euEs,
  fa: Locale.faIr,
  fi: Locale.fiFi,
  fil: Locale.filPh,
  fr: Locale.frFr,
  ga: Locale.gaIe,
  gd: Locale.gdGb,
  gl: Locale.glEs,
  gu: Locale.guIn,
  he: Locale.heIl,
  hi: Locale.hiIn,
  hr: Locale.hrHr,
  hu: Locale.huHu,
  id: Locale.idId,
  is: Locale.isIs,
  it: Locale.itIt,
  iw: Locale.heIl,
  'iw-il': Locale.heIl,
  ja: Locale.jaJp,
  ka: Locale.kaGe,
  kk: Locale.kkKz,
  km: Locale.kmKh,
  kn: Locale.knIn,
  ko: Locale.koKr,
  kok: Locale.kokIn,
  lb: Locale.lbLu,
  lo: Locale.loLa,
  lt: Locale.ltLt,
  lv: Locale.lvLv,
  mi: Locale.miNz,
  mk: Locale.mkMk,
  ml: Locale.mlIn,
  mr: Locale.mrIn,
  ms: Locale.msMy,
  mt: Locale.mtMt,
  nb: Locale.nbNo,
  ne: Locale.neNp,
  nl: Locale.nlNl,
  nn: Locale.nnNo,
  or: Locale.orIn,
  pa: Locale.paIn,
  pl: Locale.plPl,
  pt: Locale.ptBr,
  quz: Locale.quzPe,
  ro: Locale.roRo,
  ru: Locale.ruRu,
  sk: Locale.skSk,
  sl: Locale.slSi,
  sq: Locale.sqAl,
  sv: Locale.svSe,
  ta: Locale.taIn,
  te: Locale.teIn,
  th: Locale.thTh,
  tr: Locale.trTr,
  'tr-cy': Locale.enGb,
  tt: Locale.ttRu,
  ug: Locale.ugCn,
  uk: Locale.ukUa,
  ur: Locale.urPk,
  uz: Locale.enUs,
  vi: Locale.viVn,
  zh: Locale.enUs
};

/**
 * Default locale used across the office-bohemia repo.
 */
export const defaultLocale = Locale.enUs;

const languageIdRegExp = /^([^-]+)-.+/;

/**
 * Asserts a given string matches the known and supported locales
 * @param inputLocale locale identifier
 */
export function isLocale(inputLocale: string | undefined | null): inputLocale is Locale {
  if (inputLocale === undefined || inputLocale === null) {
    return false;
  }

  const lowerCaseInputLocale = inputLocale.toLowerCase();

  return (
    Object.values(Locale).includes(<Locale>lowerCaseInputLocale) || secondaryLocales[lowerCaseInputLocale] !== undefined
  );
}

/**
 * Resolve the locale from the list of allowed locales, falls back to the default locale on failure.
 */
export function resolveLocale(inputLocale: string | undefined | null): Locale {
  if (inputLocale === undefined || inputLocale === null) {
    return defaultLocale;
  }

  const lowerCaseInputLocale = inputLocale.toLowerCase();

  if (Object.values(Locale).includes(<Locale>lowerCaseInputLocale)) {
    // This is a locale that we support.
    return <Locale>lowerCaseInputLocale;
  }

  if (secondaryLocales[lowerCaseInputLocale] !== undefined) {
    // We don't support the locale but we support a valid backup.
    return secondaryLocales[lowerCaseInputLocale];
  }

  const resolvedLanguageId = resolveLanguageId(lowerCaseInputLocale);
  if (resolvedLanguageId !== undefined && secondaryLocales[resolvedLanguageId] !== undefined) {
    // We don't support the locale but we support a valid backup based on the language id.
    return secondaryLocales[resolvedLanguageId];
  }

  return defaultLocale;
}

/**
 * Resolves the language id from a given locale string, whenever possible.
 * Example: resolveLanguageId('en-us') resolves to 'en'
 */
export function resolveLanguageId(localeString: string): string | undefined {
  const possibleLanguageIdResults = languageIdRegExp.exec(localeString.trim().toLowerCase());
  if (possibleLanguageIdResults !== null && possibleLanguageIdResults.length > 1) {
    return possibleLanguageIdResults[1];
  }
  return undefined;
}
